import { useQuery } from "@tanstack/react-query";
import { coursesApi } from "@/lib/api/courses";
import { personTitle } from "@/lib/person-name";
import { zoneCity } from "@/lib/seat-workspace";
import { when } from "@/components/session/handin/hand-in-model";

/** "New York time": the course's zone as a person names it, never its identifier. */
export const courseTime = (timezone: string) => `${zoneCity(timezone)} time`;
/** "Sun, Nov 8, 8:49 AM New York time". */
export const atCourseTime = (instant: string, timezone: string) => `${when(instant, timezone)} ${courseTime(timezone)}`;

/**
 * Who saved or published, named from the course's own teaching team. The
 * signed-in person reads as "you"; anyone the team read cannot name stays
 * unnamed rather than shown by an account id.
 */
export function useTeamNames(courseId: string, enabled: boolean, me?: string): (userId: string | null | undefined) => string | null {
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => coursesApi.get(courseId), enabled: enabled && Boolean(courseId), retry: false, staleTime: 60_000 });
  return userId => {
    if (!userId) return null;
    if (userId === me) return "you";
    const member = course.data?.teaching_team?.find(person => person.faculty_user_id === userId);
    return member ? personTitle(member.display_name, member.honorific) : null;
  };
}

/** The course's current primary instructor by name: the person who publishes course rules and approves extensions. */
export function usePrimaryName(courseId: string, enabled: boolean, me?: string): string | null {
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => coursesApi.get(courseId), enabled: enabled && Boolean(courseId), retry: false, staleTime: 60_000 });
  const primary = course.data?.teaching_team?.find(person => person.assignment_kind === "primary" && !person.ended_at);
  if (!primary) return null;
  if (primary.faculty_user_id && primary.faculty_user_id === me) return "you";
  return personTitle(primary.display_name, primary.honorific);
}
